<template>
    <div class="c-textarea-wrapper">
        <label v-if="label" class="c-textarea-label" :class="labelClass" :for="id">
            {{ label }}<template v-if="required">*</template>
        </label>
        <div class="w-100">
            <textarea
                :id="id"
                v-model="model"
                class="form-control"
                :class="computedClass"
                :disabled="disabled"
                :placeholder="placeholder"
                :required="required"
                :minlength="minlength"
                :maxlength="maxlength"
                :rows="rows"
                @blur="emit('blur', model)"
                @keyup="onKeyup"
                @input="triggerDebounce"
            />
        </div>
        <c-error v-if="error" :message="errorMsg" />
    </div>
</template>

<script setup lang="ts">
import { HtmlHTMLAttributes, InputHTMLAttributes, computed } from 'vue';
import { debounce } from 'lodash';
import CError from '@/components/shared/CError.vue';

type InputSize = 'lg' | 'md' | 'sm' | 'xs';

const props = withDefaults(
    defineProps<{
        rows?: number;
        value?: string;
        id?: InputHTMLAttributes['id'];
        inputClass?: InputHTMLAttributes['class'];
        disabled?: boolean;
        required?: boolean;
        minlength?: InputHTMLAttributes['minlength'];
        maxlength?: InputHTMLAttributes['maxlength'];
        placeholder?: HtmlHTMLAttributes['placeholder'];
        label?: string;
        labelClass?: HtmlHTMLAttributes['class'];
        inputSize?: InputSize;
        error?: boolean;
        errorMsg?: string;
        debounce?: number;
    }>(),
    {
        inputSize: 'md',
        debounce: 1000,
        rows: 3
    }
);

const emit = defineEmits<{
    (event: 'input', value: string): void;
    (event: 'blur', value: string): void;
    (event: 'enter', value: string): void;
    (event: 'debounce', value: string): void;
    (event: 'keyup', value: KeyboardEvent): void;
}>();

const onKeyup = (e: KeyboardEvent) => {
    if (e.code == 'Enter' || e.key == 'Enter') {
        emit('enter', model.value);
    }
    emit('keyup', e);
};

const computedClass = computed(() => {
    const classes = [props.inputClass, `form-control-${props.inputSize}`];

    if (props.error) {
        classes.push('is-invalid');
    }

    return classes;
});

const model = computed({
    get: () => props.value || '',
    set: (value: string | undefined) => {
        emit('input', value || '');
    }
});

const triggerDebounce = debounce(() => {
    emit('debounce', model.value);
}, props.debounce);
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.c-textarea-wrapper {
    .c-textarea-label {
        font-size: 0.875rem;
        margin-bottom: 0.19rem;
    }

    textarea {
        border-color: get-color('gray-light');
        box-shadow: none;
        &::placeholder {
            color: get-color('gray');
        }
    }
}
</style>
